import dynamic from "next/dynamic";
import { useContext, useEffect } from "react";
import { newVehicles, vehicles } from "../../../../src/constants/vehicleData";
import { imagesWEBP } from "../../../../src/constants/webpImages";
import { Context } from "../../../../src/context/context";
import { sendAnalytics } from "../../../../src/utils/analyticsCommon";
import Head from "next/head";
import {
  generateProductSchema,
  createOffer,
  createBrand,
  createPropertyValue,
} from "../../../../src/utils/ProductSchemaGenerator.js";
import {
  generateFAQPageSchema,
  convertFAQsToQuestions,
} from "../../../../src/utils/FAQPageSchemaGenerator.js";
import { vehicleFAQs } from "../../../../src/constants/brandFaqs";
const CommercialVehicleLandingPage = dynamic(
  () =>
    import(
      "../../../../src/modules/CommercialVehicleLandingPage/CommercialVehicleLandingPage"
    ),
  { suspense: true }
);
import {
  TITLE,
  META_CONTENT,
  SUB_TEXT,
  MOSTLY_BOUGHT_FOR_HEADING,
  BUSINESS_BENEFIT_HEADING,
  FEATURES_HEADING,
  BRAND_DEALER_HEADING,
  BRAND_DEALER_DESCRIPITION,
  FAQ_HEADING_TEXT,
  VEHICLE_LINK,
  BREADCRUMB_ITEMS,
  BREADCRUMB_SCHEMA,
} from "../../../../src/constants/electricCommericalVehicle/Eular/hiLoadPv";

const PRICE_CITIES = [
  "pune",
  "mumbai",
  "bangalore",
  "hyderabad",
  "chennai",
  "kolkata",
  "delhi-ncr",
  "jaipur",
  "lucknow",
  "ahmedabad",
  "indore",
];

const SECONDARY_HEADER_ITEMS = [
  { label: "Euler HiLoad PV", sectionId: "section-header" },
  { label: "Price", sectionId: "section-price" },
  { label: "Specification", sectionId: "section-specification" },
  { label: "Summary", sectionId: "section-summary" },
];

export default function VehicleModelDetails({
  brand,
  vehicleModel,
  vehicleDataLocal,
  latestVehiclesInfo,
  productSchema,
  faqPageSchema,
}) {
  const noentryData = [
    {
      id: 1,
      imageName: imagesWEBP?.businesshiload0,
    },
    {
      id: 2,
      imageName: imagesWEBP?.businesshiload1,
    },
    {
      id: 3,
      imageName: imagesWEBP?.businesshiload2,
    },
    {
      id: 4,
      imageName: imagesWEBP?.businesshiload3,
    },
  ];

  const useCaseData = [
    {
      id: 1,
      imageName: imagesWEBP?.hiloadusecase0,
      title: "Use your Cargo EV across Delhi Without Restrictions",
    },
    {
      id: 2,
      imageName: imagesWEBP?.hiloadusecase1,
      title:
        "No 7 AM to 11 AM and 5PM to 9PM Restrictions for ELECTRIC VEHICLES IN Delhi",
    },
    {
      id: 3,
      imageName: imagesWEBP?.hiloadusecase2,
      title: "Benefit from Govt. Relaxation for EVs",
    },
    {
      id: 3,
      imageName: imagesWEBP?.hiloadusecase3,
      title: "Benefit from Govt. Relaxation for EVs",
    },
  ];

  const images = [
    {
      original: imagesWEBP?.rating1,
    },
    {
      original: imagesWEBP?.rating2,
    },
    {
      original: imagesWEBP?.rating3,
    },
    { original: imagesWEBP?.rating4 },
  ];

  useEffect(() => {
    sendAnalytics(
      "screen_viewed",
      { screen_name: `electric_commercial_vehicle_${brand}_${vehicleModel}` },
      "en"
    );
  }, [brand, vehicleModel]);

  const state = useContext(Context);
  let location =
    state?.selectedState === "Others" ? "India" : state?.selectedState;

  //We need to add SEO Meta info later
  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(productSchema),
          }}
          defer
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(faqPageSchema),
          }}
          defer
        />
      </Head>
      <CommercialVehicleLandingPage
        title={TITLE}
        metaContent={META_CONTENT}
        vehicleLink={VEHICLE_LINK}
        SUB_TEXT={SUB_TEXT}
        MOSTLY_BOUGHT_FOR_HEADING={MOSTLY_BOUGHT_FOR_HEADING}
        BUSINESS_BENEFIT_HEADING={BUSINESS_BENEFIT_HEADING}
        FEATURES_HEADING={FEATURES_HEADING}
        BRAND_DEALER_HEADING={BRAND_DEALER_HEADING}
        BRAND_DEALER_DESCRIPITION={BRAND_DEALER_DESCRIPITION}
        FAQ_HEADING_TEXT={FAQ_HEADING_TEXT}
        location={location}
        vehicleDataLocal={vehicleDataLocal}
        vehicleName="hiLoadDV"
        useCaseData={useCaseData}
        noentryData={noentryData}
        latestVehiclesInfo={latestVehiclesInfo}
        H1_HEADING={"Euler HiLoad EV - Pickup van"}
        BREADCRUMB_ITEMS={BREADCRUMB_ITEMS}
        BREADCRUMB_SCHEMA={BREADCRUMB_SCHEMA}
        secondaryHeaderItems={SECONDARY_HEADER_ITEMS}
        priceCities={PRICE_CITIES}
        pricePathPrefix="Euler/HiLoadPV"
      />
    </>
  );
}

export const getStaticProps = (context) => {
  let vehicleData = [...vehicles, ...newVehicles];

  const brand = "Euler";
  const vehicleModel = "HiLoadPV";

  let vehicleDataLocal = vehicleData?.filter(function (vehicle) {
    return vehicle?.vehicle_path_id === `${vehicleModel}`;
  });

  let latestVehiclesInfo = vehicleData?.filter((vehicle) => {
    return vehicle?.vehicle_path_id !== `${vehicleModel}`;
  });

  // Constants are already imported at the top of the file

  // Generate Product Schema
  const vehicleDataItem = vehicleDataLocal?.[0];
  const productImages = vehicleDataItem?.images_287 || [
    imagesWEBP?.businesshiload0,
    imagesWEBP?.businesshiload1,
    imagesWEBP?.businesshiload2,
    imagesWEBP?.businesshiload3,
  ].filter(Boolean);

  // Get price range from vehicle data
  const getPriceRange = () => {
    if (!vehicleDataItem?.prices) return { lowPrice: 426000, highPrice: 446000 };
    const allPrices = [];
    Object.values(vehicleDataItem.prices).forEach((statePrices) => {
      Object.values(statePrices).forEach((variant) => {
        if (variant.price) {
          allPrices.push(variant.price * 100000); // Convert lakhs to rupees
        }
      });
    });
    return {
      lowPrice: Math.min(...allPrices),
      highPrice: Math.max(...allPrices),
    };
  };

  const priceRange = getPriceRange();

  const productSchema = generateProductSchema({
    name: "Euler HiLoad PV",
    description: SUB_TEXT || META_CONTENT[0]?.content,
    image: productImages,
    url: VEHICLE_LINK,
    brand: createBrand({
      name: "Euler",
      url: "https://www.turno.club/electric-commercial-vehicles/Euler",
    }),
    category: "Electric Commercial Vehicle",
    model: "HiLoad PV",
    manufacturer: {
      "@type": "Organization",
      name: "Euler Motors Private Limited",
    },
    offers: createOffer({
      offerCount: 3,
      lowPrice: priceRange.lowPrice,
      highPrice: priceRange.highPrice,
      priceCurrency: "INR",
      availability: "https://schema.org/InStock",
      url: VEHICLE_LINK,
      seller: {
        "@type": "Organization",
        name: "Turno",
        url: "https://www.turno.club/",
      },
    }),
    additionalProperty: [
      vehicleDataItem?.load
        ? createPropertyValue({ name: "Load Capacity", value: vehicleDataItem.load })
        : null,
      vehicleDataItem?.range
        ? createPropertyValue({ name: "Range", value: vehicleDataItem.range })
        : null,
      vehicleDataItem?.batteryCapacity
        ? createPropertyValue({
            name: "Battery Capacity",
            value: vehicleDataItem.batteryCapacity,
          })
        : null,
      vehicleDataItem?.batteryVoltage
        ? createPropertyValue({
            name: "Battery Voltage",
            value: vehicleDataItem.batteryVoltage,
          })
        : null,
    ].filter(Boolean),
    keywords: META_CONTENT[1]?.content || "Euler HiLoad PV, electric vehicle",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": VEHICLE_LINK,
    },
    itemCondition: "https://schema.org/NewCondition",
    isFamilyFriendly: true,
    slogan: "High-capacity electric three-wheeler pickup van",
  });

  // Generate FAQPage Schema
  const faqQuestions = convertFAQsToQuestions(vehicleFAQs?.hiLoadDV || [], {
    url: VEHICLE_LINK,
    dateCreated: new Date().toISOString(),
    author: {
      "@type": "Organization",
      name: "Turno",
      url: "https://www.turno.club/",
    },
  });

  const faqPageSchema = generateFAQPageSchema({
    mainEntity: faqQuestions,
    name: FAQ_HEADING_TEXT || "FAQs on Euler HiLoad PV",
    description: `Frequently asked questions about Euler HiLoad PV electric commercial vehicle. ${SUB_TEXT || ""}`,
    url: VEHICLE_LINK,
    headline: FAQ_HEADING_TEXT || "FAQs on Euler HiLoad PV",
    keywords: META_CONTENT[1]?.content || "Euler HiLoad PV, FAQ, electric vehicle",
    inLanguage: "en",
    publisher: {
      "@type": "Organization",
      name: "Turno",
      url: "https://www.turno.club/",
      logo: {
        "@type": "ImageObject",
        url: "https://www.turno.club/logoIcon.png",
      },
    },
    datePublished: new Date().toISOString(),
    dateModified: new Date().toISOString(),
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": VEHICLE_LINK,
    },
    breadcrumb: BREADCRUMB_SCHEMA,
    primaryImageOfPage: {
      "@type": "ImageObject",
      url: productImages[0] || "https://www.turno.club/logoIcon.png",
    },
    isFamilyFriendly: true,
    isAccessibleForFree: true,
    speakable: {
      "@type": "SpeakableSpecification",
      xPath: [
        "/html/head/title",
        "/html/head/meta[@name='description']/@content",
      ],
    },
  });

  return {
    props: { 
      brand, 
      vehicleModel, 
      vehicleDataLocal, 
      latestVehiclesInfo,
      productSchema,
      faqPageSchema,
    },
  };
};
